import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import ErrorField from '../../components/ErrorField';
import Icon, { Icons } from '../../components/Icon/Icon';
import Input from '../../components/Input';
import PasswordInput from '../../components/PasswordInput/PasswordInput';
import ShortErrorInfo from '../../components/ShortErrorInfo';
import AppConfig from '../../config';
import useBackButton from '../../hooks/useBackButton';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import WebSocketService from '../../services/WebSocketService';
import { appSlice } from '../../store/slices';
import AppVersion from '../AppVersion';

import { getDeviceInfo } from '../../hooks/useDeviceInfo';
import './Auth.scss';

const { routes } = AppConfig;

const minut = 60;
const iconSizeEditPen = 20;

const Auth = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const intl = useIntl();
	const [login, setLogin] = useState();
	const [pass, setPass] = useState();
	const [authError, setAuthError] = useState('');
	const [loginAllowedTime, setLoginAllowedTime] = useState(null);
	const [remainingLockTime, setRemainingLockTime] = useState(null);

	const timerIdRef = useRef(null);
	const url = useSelector(state => state?.app?.url);
	const hash = useSelector(state => state?.app?.hash);
	const initDataTG = useSelector(state => state.app.initDataTG);

	const startTimer = () => {
		const doTick = () => {
			const timeLeft = Math.floor((loginAllowedTime - Date.now()) / 1000);
			if (timeLeft < 0) {
				clearInterval(timerIdRef.current);
			} else {
				setRemainingLockTime(timeLeft);
			}
		};

		timerIdRef.current = setInterval(() => {
			doTick();
		}, 1000);
	};

	useEffect(() => {
		if (loginAllowedTime) startTimer();

		return () => {
			clearInterval(timerIdRef.current);
		};
	}, [loginAllowedTime]); // eslint-disable-line react-hooks/exhaustive-deps

	const onLogin = async () => {
		try {
			const deviceInfo = await getDeviceInfo();
			const { fingerprintId } = deviceInfo;
			const response = await RequestService.Login(login.trim(), pass.trim(), initDataTG, fingerprintId);

			if (response?.Status !== 'Success') {
				if (response?.ErrorCode === 'AccountBlocked') {
					navigate(routes.error, { state: { name: 'ErrorBlocked' } });
				} else if (response?.ErrorCode === 'LoginValidateLimitReachedError') {
					setLoginAllowedTime(Date.now() + response.Error.SecondsLeft * 1000 + 2000);
				} else if (response?.ErrorCode === 'LoginValidateError') {
					setAuthError(t('Login or password is incorrect'));
				} else if (response?.ErrorCode === 'InvalidTelegramAccountTryLoginError') {
					navigate(routes.error, { state: { name: 'InvalidTelegramAccountError' } });
				} else if (response.Error) {
					HelperService.showNotification({
						type: 'error',
						body: <ShortErrorInfo error={response.Error} />,
						autoHideDuration: 10000,
					});
				} else {
					throw Error(response);
				}
			} else {
				dispatch(appSlice.actions.setToken(response.Token));
				navigate(routes.portfolio);
				try {
					WebSocketService.DoConnection(url, response.Token);
				} catch (error) {
					console.log(error);
				}
			}
		} catch (error) {
			HelperService.showNotification({
				type: 'error',
				body: <ShortErrorInfo error={error} />,
				autoHideDuration: 10000,
			});
		}
	};

	useEffect(() => {
		const getUrl = async () => {
			const newUrl = await HelperService.decryptHash(hash);

			dispatch(appSlice.actions.setUrl(newUrl + (newUrl.at(-1) === '/' ? '' : '/')));
		};

		hash && getUrl();
	}, [hash]); // eslint-disable-line react-hooks/exhaustive-deps

	useBackButton(false);

	const openModal = () =>
		dispatch(
			appSlice.actions.updateModal({
				title: t('Re-enter hash'),
				tag: 'DeleteHash',
				isOpen: true,
				outsideParams: {},
			})
		);

	const hashPostfix = () => (
		<div className='btn-delete-hash' onClick={() => openModal()}>
			<Icon type={Icons.EditPen} width={iconSizeEditPen} height={iconSizeEditPen} />
		</div>
	);

	return (
		<div className='Auth'>
			<div className='title'>{t('Login')}</div>
			<div className='subtitle'>{t('Enter your login and password')}</div>

			<Input
				label={t('Enter your login')}
				placeholder={intl.formatMessage({ id: 'Login' })}
				value={login}
				onChange={e => {
					setAuthError('');
					setLogin(e.target.value.replace(/\s/g, ''));
				}}
				error={authError}
			/>

			<PasswordInput
				label={t('Enter your password')}
				placeholder={intl.formatMessage({ id: 'Password' })}
				value={pass}
				onChange={e => {
					setAuthError('');
					setPass(e.target.value.trim());
				}}
				error={authError}
			/>

			<Input label={t('Hash')} className='hash-input' value={hash} disabled={true} postfix={hashPostfix()} />

			{/* <QrInput
				className='StepLogin__qr'
				label='Enter has or scan QR code'
				placeholder={intl.formatMessage({ id: 'Hash' })}
				value={hash}
				onChange={e => setHash(e.target.value)}
				error={authError}
			/> */}

			{remainingLockTime ? (
				<ErrorField
					element={t('Exceeded the number of registration attempts! Login blocked for {min} min {sec} sec', {
						min: Math.floor(remainingLockTime / minut),
						sec: remainingLockTime % minut,
					})}
					className='StepLogin__error-filed'
				/>
			) : null}

			{authError ? <ErrorField element={authError} className='StepLogin__error-filed' /> : null}

			<AppVersion />

			{/* <Button onClick={() => navigate(routes.authNew)}>New AUTH</Button> */}
			<Button onClick={onLogin} disabled={!login || !pass || !url || !!authError || remainingLockTime > 0}>
				{t('Continue')}
			</Button>
		</div>
	);
};

export default Auth;
